"use client";

import type { ForumAuthor } from "./forum.types";
import * as stylex from "@stylexjs/stylex";
import { styles } from "./forum.styles";

const timestampFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
});

function relativeAge(iso: string) {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60_000),
  );

  if (minutes < 2) return "just now";

  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);

  if (days < 7) return `${days}d ago`;

  if (days < 35) return `${Math.floor(days / 7)}w ago`;

  if (days < 365) return `${Math.min(11, Math.floor(days / 30))}mo ago`;

  return `${Math.floor(days / 365)}y ago`;
}

export function Byline({
  author,
  createdAt,
  updatedAt,
  commentCount,
  renderLink,
}: Readonly<{
  author: ForumAuthor;
  createdAt: string;
  updatedAt?: string | null | undefined;
  commentCount?: number | null | undefined;
  renderLink?: import("./forum.types").ForumLinkRenderer;
}>) {
  return (
    <p {...stylex.props(styles.byline)}>
      {author.href && renderLink ? (
        renderLink({
          href: author.href,
          ...stylex.props(styles.author, styles.link),
          children: author.name,
        })
      ) : (
        <span {...stylex.props(styles.author)}>{author.name}</span>
      )}
      {author.type === "player" && (
        <>
          {" "}
          · <span {...stylex.props(styles.player)}>player</span>
        </>
      )}
      {author.karma != null && (
        <> · {author.karma.toLocaleString("en-US")} karma</>
      )}
      {" · "}
      <time
        dateTime={createdAt}
        title={timestampFormatter.format(new Date(createdAt))}
      >
        {relativeAge(createdAt)}
      </time>
      {updatedAt && (
        <>
          {" "}
          ·{" "}
          <time
            dateTime={updatedAt}
            title={timestampFormatter.format(new Date(updatedAt))}
          >
            edited
          </time>
        </>
      )}
      {commentCount != null && (
        <>
          {" "}
          · {commentCount} {commentCount === 1 ? "comment" : "comments"}
        </>
      )}
    </p>
  );
}
